import { Download } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { useLabelExport } from '../hooks/useLabelExport';
import type { LabelingExperiment } from '../types/LabelingTypes';

interface LabelExportButtonProps {
  /** Every experiment to include — the export always covers the whole list. */
  experiments: LabelingExperiment[];
  disabled?: boolean;
}

/**
 * Downloads every annotator's labeled data of all experiments as an Excel workbook — an
 * admin-only action: it renders nothing, not even its own strip, for anyone else. The fetch
 * spans one request per experiment, so the button reports its progress while it runs.
 */
export function LabelExportButton({ experiments, disabled = false }: LabelExportButtonProps) {
  const { t } = useTranslation();
  const { canExport, status, progress, exportLabels } = useLabelExport();
  const exporting = status === 'exporting';

  if (!canExport) return null;

  return (
    <div className="flex flex-col gap-1.5 border-t border-border p-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full gap-2"
        disabled={disabled || experiments.length === 0}
        loading={exporting}
        title={t('labeling.export.hint')}
        onClick={() => void exportLabels(experiments)}
        data-testid="label-export-button"
      >
        {!exporting && <Download className="size-4" aria-hidden />}
        {exporting && progress
          ? t('labeling.export.inProgress', { done: progress.done, total: progress.total })
          : t('labeling.export.button')}
      </Button>
      {status === 'empty' && (
        <Callout size="inline" tone="neutral" role="status">
          {t('labeling.export.empty')}
        </Callout>
      )}
      {status === 'error' && (
        <Callout size="inline" tone="error">
          {t('labeling.export.error')}
        </Callout>
      )}
    </div>
  );
}
